<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form">				
				<h2>Группы сервисных работ</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddCategoryClick">
					Новая группа
				</el-button>
			</div>
		</el-col>
	</el-row>
	
	<div class="base-form">			
		<el-row>
			<el-col :span="24">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" clearable placeholder="Поиск по названию группы сервисных работ"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</el-col>
		</el-row>
	</div>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 				
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"				
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">
				<template #empty>
					Список групп пуст, попробуйте изменить фильтр
				</template>
				<el-table-column v-if="!isPickerMode" type="selection" />
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onPickJobCategoryClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Категория" prop="name" sortable column-key="name" />
				<el-table-column label="Описание" prop="description" column-key="description" />		
				<el-table-column v-if="!isPickerMode" align="right">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onDeleteJobGroupClick(scope.row.id, scope.row.name, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
	<el-row v-if="isPickerMode">
		<el-col>
			<div class="base-form">
				<el-button @click="onCloseCurrentTabClick">
					Отмена
				</el-button>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ServiceJobCategoryModel from '@/models/ServiceJobCategoryModel';
import ServiceJobGroupModel from '@/models/ServiceJobGroupModel';
import JobGroupsApiHelper from '@/helpers/api/JobGroupsApiHelper';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class ServiceJobGroupsView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	isPickerMode = false;
	onPickedCallback: Function | null = null;
	circleIcon = CirclePlus;
	searchIcon = Search;
	
	/** view-models */
	tableLoading = false;
	tableData: Array<ServiceJobGroupModel> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';

	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.fetchJobGroupsAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobGroupsAsync()
		);

		EventHelper.setEventListener('jobGroupsUpdate', () => this.fetchJobGroupsAsync());
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		EventHelper.removeEventListener('jobGroupsUpdate', () => this.fetchJobGroupsAsync());
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}
	}

	onTableRowClick(row: ServiceJobOperationModel) {
		if (this.isPickerMode) {
			return;
		}
		const viewTab = TabsDictionary.jobGroupView;		
		viewTab.params = [ row, false ];
		store.commit('addTab', viewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddCategoryClick() {
		const addTab = TabsDictionary.jobGroupAdd;
		const addNewCategory = true;
		addTab.params = [ null, addNewCategory ];
		store.commit('addTab', addTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobGroupsAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobgroups', { params: filterObject });
			this.tableData = response.data.map((_: any) => new ServiceJobCategoryModel(_));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить группы сервисных работ`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузки групп сервисных работ`,
					type: 'error',
				});
			}
			console.error('Error loading service-job-groups list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteJobGroupAsync(groupId: number) {
		try {
			this.tableLoading = true;
			const jwt = store.getters.jwt;
			const jobOperationsHttpClient = axiosHelper.createInstance(jwt);
			await jobOperationsHttpClient.delete(`/api/servicejobgroups/${groupId}`);
			ElNotification({
				title: 'Группа сервисных работ удалена',
				type: 'success',
			});	
			EventHelper.dispatchEvent('jobGroupsUpdate');
		} catch (e) {			
			ElNotification({
				title: 'Ошибка, группа сервисных работ не была удалена',
				type: 'error',
			});	
			console.error(`Error deleting service-job-group #${groupId}`, e)
		} finally {
			this.tableLoading = false;
		}		
	}

	async onDeleteJobGroupClick(groupId: number, groupName: string, e: Event) {
		e.stopPropagation();

		const jobGroupsApiHelper = new JobGroupsApiHelper();
		const relatedJobIdsToGroup = await jobGroupsApiHelper.getRelatedJobIdsAsync(store.getters.jwt, groupId);

		const confirmMessage = relatedJobIdsToGroup?.length === 0 ? 
			`Группа сервисных работ '${groupName}' будет удалена` : 
			`В группе '${groupName}' есть сервисные работы (${relatedJobIdsToGroup?.length}). При удалении группы все работы будут перемещены в группу по-умолчанию`

		ElMessageBox.confirm(
			confirmMessage,
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(() => this.deleteJobGroupAsync(groupId));
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobGroupsAsync();
	}

	/** handles callback and passes selected category (if picker-mode is enabled) */	
	onPickJobCategoryClick(category: ServiceJobCategoryModel, e: Event) {
		e.stopPropagation();
		if (!this.onPickedCallback) {
			return;
		}
		this.onPickedCallback(category);
	}

	onCloseCurrentTabClick() {		
		const currentTabId = store.getters.tab;
		store.commit('removeTab', currentTabId);
	}
}
</script>